import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { LuMapPin, LuClock, LuSend, LuNavigation, LuTriangleAlert, LuHash, LuMessageCircle, LuCalendarDays, LuCar, LuCircleCheck } from 'react-icons/lu';
import api from '../../services/api';
import ModalShell from '../ui/ModalShell';
import InformeTrabajo from './InformeTrabajo';
import TrabajoHecho from './TrabajoHecho';
import CancelarPedido from './CancelarPedido';
import { VerFotos } from './FotosPedido';
import CargaPorSerieSheet from '../ordenes/CargaPorSerieSheet';
import PrioridadPedido from './PrioridadPedido';
import NuevoPedidoSheet from './NuevoPedidoSheet';
import CambiarHorarioSheet from './CambiarHorarioSheet';
import ClienteNoPuedeSheet from './ClienteNoPuedeSheet';
import { estadoDe, cuandoPedido, linkMaps, haceCuanto, PASOS_PEDIDO, esAbierto } from '../../utils/pedidosEmpresa';
import { resumenVentanas, salirAhoraACoordinar } from '../../utils/ordenes';
import ConfirmarHorarioSheet from '../servicio/ConfirmarHorarioSheet';
import ConfirmDialog from '../ui/ConfirmDialog';

// Ficha de un pedido (Portal Empresa, 7-oct-2026). La usan los dos lados:
// la empresa (modo="empresa") y el admin (modo="admin", con Agendar / Cancelar).
// La conversación del pedido reemplaza los comentarios de Trello.
export default function PedidoDetalle({ pedido: inicial, modo = 'empresa', funciones = null, onCerrar, onCambio, onAgendar }) {
    const base = modo === 'empresa' ? '/empresa/pedidos' : '/pedidos-empresa';
    const [p, setP] = useState(inicial);
    const [cambiandoHorario, setCambiandoHorario] = useState(false);
    const [noPuede, setNoPuede] = useState(false);   // 4-nov-2026
    const [confirmando, setConfirmando] = useState(false);
    const [comentarios, setComentarios] = useState([]);
    const [informe, setInforme] = useState([]);
    const [texto, setTexto] = useState('');
    const [enviando, setEnviando] = useState(false);
    const [cancelando, setCancelando] = useState(false);
    const [moviendo, setMoviendo] = useState(false);
    const [editando, setEditando] = useState(false);
    const [eliminando, setEliminando] = useState(false);   // admin: pedidos de prueba o cargados por error
    const [cerrando, setCerrando] = useState(null); // la visita, para "Cerrar trabajo"
    const [eligiendoDia, setEligiendoDia] = useState(null); // la visita "a coordinar", para elegir el día
    const finRef = useRef(null);
    useEffect(() => { setP(inicial); }, [inicial]); // al agendar llega el pedido actualizado

    const cargar = useCallback(async () => {
        try {
            const [r1, r2] = await Promise.all([api.get(`${base}/${inicial.id}`), api.get(`${base}/${inicial.id}/comentarios`)]);
            setP(r1.data);
            setComentarios(Array.isArray(r2.data) ? r2.data : []);
        } catch { /* queda lo que había */ }
    }, [base, inicial.id]);

    useEffect(() => { cargar(); const id = setInterval(cargar, 15000); return () => clearInterval(id); }, [cargar]);
    // Etapa 2: cuando la visita quedó hecha, el informe (qué se hizo, repuestos, fotos)
    const hecho = p.estado === 'HECHO';
    useEffect(() => {
        if (!hecho) return;
        api.get(`${base}/${inicial.id}/informe`).then(r => setInforme(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    }, [hecho, base, inicial.id]);
    useEffect(() => { finRef.current?.scrollIntoView({ block: 'end' }); }, [comentarios.length]);

    // M12: candado inmediato (el estado "enviando" tarda un render y dos Enter rápidos pasaban)
    const enviandoRef = useRef(false);
    const enviar = async () => {
        const t = texto.trim();
        if (!t || enviandoRef.current) return;
        enviandoRef.current = true;
        setEnviando(true);
        try {
            const r = await api.post(`${base}/${p.id}/comentarios`, { texto: t });
            setComentarios(c => [...c, r.data]);
            setTexto('');
            onCambio && onCambio();
        } catch (e) {
            toast.error(e?.response?.data?.mensaje || e?.response?.data?.message || 'No se pudo enviar');
        } finally { enviandoRef.current = false; setEnviando(false); }
    };

    const cancelar = async (motivo) => {
        try {
            const r = modo === 'empresa'
                ? await api.patch(`${base}/${p.id}/cancelar`, { motivo })
                : await api.patch(`${base}/${p.id}/rechazar`, { motivo });
            setP(r.data);
            setCancelando(false);
            cargar(); // trae el mensaje "Pedido cancelado: …" a la conversación
            toast.success('Pedido cancelado');
            onCambio && onCambio();
        } catch (e) {
            toast.error(e?.response?.data?.mensaje || e?.response?.data?.message || 'No se pudo cancelar');
        }
    };

    // Eliminar del todo (admin, 22-oct-2026): pedidos de prueba o cargados por error. La visita, si
    // estaba abierta, se cancela y sale de la agenda del técnico; a la empresa no le llega aviso.
    const eliminar = async () => {
        try {
            await api.delete(`/pedidos-empresa/${p.id}`);
            toast.success('Pedido eliminado');
            setEliminando(false);
            onCambio && onCambio();
            onCerrar && onCerrar();
        } catch (e) {
            toast.error(e?.response?.data?.mensaje || 'No se pudo eliminar');
            setEliminando(false);
        }
    };

    // Pasos de la visita desde el admin (10-oct-2026): lo mismo que hace el técnico
    // desde su usuario. A la empresa le llegan los mismos avisos.
    const PASO_VISITA = {
        AGENDADO:  { estado: 'EN_CAMINO', label: 'Salió', Icon: LuCar },
        EN_CAMINO: { estado: 'EN_SITIO', label: 'Llegó', Icon: LuMapPin, atras: 'PENDIENTE' },
        EN_CURSO:  { cerrar: true, label: 'Cerrar trabajo', Icon: LuCircleCheck, atras: 'EN_CAMINO' },
    };
    // Día a coordinar (15-oct-2026): antes no había ningún botón. "Salgo ahora" fija hoy y la hora
    // actual (si está dentro de lo que marcó la empresa) y la pone En camino; o se elige otro día.
    const aCoordinar = modo === 'admin' && p.ordenId && !p.fecha && p.estado === 'AGENDADO';
    const paso = aCoordinar ? { salirAhora: true, label: 'Salgo ahora', Icon: LuCar }
        : modo === 'admin' && p.ordenId && p.fecha ? PASO_VISITA[p.estado] : null;
    const abrirElegirDia = async () => {
        try { const r = await api.get(`/ordenes/${p.ordenId}`); setEligiendoDia(r.data); }
        catch { toast.error('No se pudo abrir la visita'); }
    };
    const salirAhora = async () => {
        setMoviendo(true);
        try {
            await salirAhoraACoordinar(api, p.ordenId);
            toast.success('En camino: le avisamos a la empresa');
            await cargar();
            onCambio && onCambio();
        } catch (e) {
            toast.error(e?.response?.data?.mensaje || 'No se pudo marcar la salida', { duration: 6000 });
            abrirElegirDia();
        } finally { setMoviendo(false); }
    };
    const moverVisita = async (estado) => {
        setMoviendo(true);
        try {
            await api.patch(`/ordenes/${p.ordenId}/estado`, { estado });
            await cargar();
            onCambio && onCambio();
        } catch (e) {
            toast.error(e?.response?.data?.mensaje || e?.response?.data?.message || 'No se pudo actualizar la visita');
        } finally { setMoviendo(false); }
    };
    const abrirCierre = async () => {
        try {
            const r = await api.get(`/ordenes/${p.ordenId}`);
            setCerrando(r.data);
        } catch { toast.error('No se pudo abrir la visita'); }
    };

    const est = estadoDe(p);
    const cuando = cuandoPedido(p);
    const sinVisitaActiva = !p.ordenId || ['NO_ATENDIDO', 'PAUSADO'].includes(p.estado);
    // Empresa: sin visita o «a coordinar» (el técnico todavía no confirmó el día) — 22-oct-2026
    const puedeCancelar = modo === 'admin' ? esAbierto(p) && sinVisitaActiva : (p.cancelable ?? (p.estado === 'NUEVO' && !p.ordenId));
    // La empresa puede cambiar su pedido mientras no esté agendado (10-oct-2026)
    const puedeEditar = modo === 'empresa' && p.estado === 'NUEVO' && !p.ordenId;
    // Reclamo de la empresa (8-oct-2026): se puede volver a agendar una revisión
    const puedeAgendar = modo === 'admin' && ((esAbierto(p) && sinVisitaActiva) || (p.estado === 'HECHO' && (p.conformidad === 'PROBLEMA' || (p.conformidad === 'OBSERVADO' && p.observacionEstado === 'PENDIENTE'))));

    return (
        <ModalShell titulo={`${p.motivo || 'Pedido'}${p.urgente ? ' · urgente' : ''}`}
            subtitulo={`Pedido #${p.id}${modo === 'admin' && p.clienteNombre ? ' · ' + p.clienteNombre : ''} · ${haceCuanto(p.creadoEn)}${p.creadoPorNombre ? ' · ' + p.creadoPorNombre : ''}`}
            onCerrar={onCerrar} ancho="md:max-w-xl"
            pie={esAbierto(p) || comentarios.length ? (
                <div className="space-y-2">
                    <div className="flex items-end gap-2">
                        <textarea rows={1} value={texto} onChange={e => setTexto(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar(); } }}
                            placeholder={modo === 'empresa' ? 'Escribinos algo sobre este pedido…' : 'Responderle a la empresa…'}
                            className="flex-1 min-h-[44px] max-h-32 px-3.5 py-2.5 rounded-xl bg-chip text-body text-ink outline-none resize-none" />
                        <button type="button" onClick={enviar} disabled={!texto.trim() || enviando} aria-label="Enviar"
                            className="w-11 h-11 shrink-0 rounded-xl bg-[#C9341F] text-white flex items-center justify-center active:scale-95 disabled:opacity-40"><LuSend size={18} /></button>
                    </div>
                    {cancelando && puedeCancelar ? (
                        <CancelarPedido modo={modo} onConfirmar={cancelar} onVolver={() => setCancelando(false)} />
                    ) : (puedeAgendar || puedeCancelar) && (
                        <div className="flex gap-2">
                            {puedeEditar && <button type="button" onClick={() => setEditando(true)} className="flex-1 h-11 rounded-xl bg-chip text-ink text-label font-black active:scale-95">Editar pedido</button>}
                            {puedeCancelar && <button type="button" onClick={() => setCancelando(true)} className="flex-1 h-11 rounded-xl bg-chip text-secondary text-label font-black active:scale-95">Cancelar pedido</button>}
                            {puedeAgendar && <button type="button" onClick={() => onAgendar && onAgendar(p)} className="flex-[2] h-11 rounded-xl bg-[#C9341F] text-white text-label font-black active:scale-95">{(p.conformidad === 'PROBLEMA' || p.conformidad === 'OBSERVADO') ? 'Agendar revisión' : p.ordenId ? 'Volver a agendar' : 'Agendar visita'}</button>}
                        </div>
                    )}
                </div>
            ) : null}>
            <div className="space-y-4">
                {/* Estado */}
                <div className="rounded-2xl bg-panel p-4 space-y-3">
                    <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ background: est.color }} />
                        <span className="text-body font-black text-ink">{est.label}</span>
                        {p.urgente && <span className="ml-auto inline-flex items-center gap-1 text-caption font-black text-brand-red"><LuTriangleAlert size={13} /> Urgente</span>}
                    </div>
                    {est.paso >= 0 && (
                        <div className="flex gap-1">
                            {PASOS_PEDIDO.map((paso, i) => (
                                <div key={paso} className="flex-1 min-w-0">
                                    <div className="h-1.5 rounded-full" style={{ background: i <= est.paso ? est.color : 'var(--chip, rgba(0,0,0,0.08))' }} />
                                    <p className={`mt-1 text-[11px] font-bold truncate ${i <= est.paso ? 'text-ink' : 'text-muted'}`}>{paso}</p>
                                </div>
                            ))}
                        </div>
                    )}
                    {cuando && <p className="flex items-center gap-2 text-label font-bold text-secondary"><LuClock size={15} className="shrink-0 text-muted" />{p.aConfirmar ? 'Propuesto: ' : ''}{cuando}</p>}
                    {/* 4-nov-2026: el día que propusimos, para que la empresa lo confirme con su cliente */}
                    {p.aConfirmar && (
                        <div className="space-y-2 pt-1">
                            <span className="inline-flex items-center h-6 px-2 rounded-md text-caption font-black bg-[var(--warning-bg)] text-[var(--warning-tx)]">⏳ {modo === 'empresa' ? 'A coordinar con tu cliente' : 'Falta que la empresa confirme'}</span>
                            {modo === 'empresa' && (
                                <>
                                    <button type="button" disabled={confirmando} onClick={async () => {
                                        setConfirmando(true);
                                        try {
                                            const r = await api.post(`/empresa/pedidos/${p.id}/cliente-confirmo`);
                                            setP(r.data); toast.success('¡Gracias! Le avisamos al técnico'); cargar(); onCambio && onCambio();
                                        } catch (e) { toast.error(e?.response?.data?.mensaje || 'No se pudo confirmar'); }
                                        finally { setConfirmando(false); }
                                    }} className="w-full h-11 rounded-xl bg-[#16A34A] text-white text-label font-black active:scale-95 disabled:opacity-40">✓ El cliente confirmó</button>
                                    <button type="button" onClick={() => setNoPuede(true)}
                                        className="w-full h-11 rounded-xl bg-chip text-ink text-label font-black active:scale-95">No puede ese día</button>
                                </>
                            )}
                        </div>
                    )}
                    {paso && (
                        <div className="flex gap-2 pt-1">
                            <button type="button" disabled={moviendo} onClick={() => (paso.salirAhora ? salirAhora() : paso.cerrar ? abrirCierre() : moverVisita(paso.estado))}
                                className="flex-[2] h-11 rounded-xl bg-[#C9341F] text-white text-label font-black inline-flex items-center justify-center gap-1.5 active:scale-95 disabled:opacity-40">
                                <paso.Icon size={16} /> {paso.label}
                            </button>
                            {paso.atras && (
                                <button type="button" disabled={moviendo} onClick={() => moverVisita(paso.atras)}
                                    className="flex-1 h-11 rounded-xl bg-chip text-secondary text-label font-black active:scale-95 disabled:opacity-40">Deshacer</button>
                            )}
                            {paso.salirAhora && (
                                <button type="button" disabled={moviendo} onClick={abrirElegirDia}
                                    className="flex-1 h-11 rounded-xl bg-chip text-ink text-label font-black active:scale-95 disabled:opacity-40">Elegir otro día</button>
                            )}
                        </div>
                    )}
                </div>

                {modo === 'admin' && esAbierto(p) && p.prioridad && (
                    <PrioridadPedido pedido={p} onCambio={nuevo => { setP(nuevo); onCambio && onCambio(); }} />
                )}

                {/* Datos */}
                <div className="space-y-2">
                    <a href={linkMaps(p.direccion)} target="_blank" rel="noreferrer" className="flex items-start gap-2.5 p-3 rounded-2xl bg-chip active:scale-[0.99]">
                        <LuMapPin size={17} className="shrink-0 mt-0.5 text-brand-red" />
                        <span className="flex-1 min-w-0">
                            {p.lugar && p.lugar !== p.direccion && <span className="block text-body font-black text-ink">{p.lugar}</span>}
                            <span className="block text-label text-secondary">{p.direccion}</span>
                        </span>
                        <LuNavigation size={16} className="shrink-0 mt-0.5 text-muted" />
                    </a>
                    {/* Carga guiada (10-oct-2026): cada equipo con lo que le pasa */}
                    {p.equipos?.length > 0 ? (
                        <div className="rounded-2xl bg-chip divide-y divide-black/[0.05] dark:divide-white/[0.06]">
                            {p.equipos.map((e, i) => (
                                <div key={i} className="flex items-start gap-2.5 px-3 py-2.5">
                                    <LuHash size={15} className="shrink-0 mt-0.5 text-muted" />
                                    <span className="flex-1 min-w-0">
                                        <span className="block text-label font-black text-ink">{e.serie ? `N/S ${e.serie}` : 'Sin N/S'}
                                            {(e.modelo || e.ubicacion) && <span className="font-bold text-muted"> · {[e.modelo, e.ubicacion].filter(Boolean).join(' · ')}</span>}</span>
                                        <span className="block text-caption text-secondary">{e.motivo}</span>
                                    </span>
                                </div>
                            ))}
                        </div>
                    ) : p.equipoSerie && <p className="flex items-center gap-2 px-1 text-label font-bold text-secondary"><LuHash size={15} className="text-muted" />Equipo N/S {p.equipoSerie}</p>}
                    {p.ventanas?.length > 0 && esAbierto(p) && (
                        <div className="flex items-start gap-2.5 px-1">
                            <LuCalendarDays size={15} className="shrink-0 mt-0.5 text-muted" />
                            <span className="flex-1 min-w-0">
                                <span className="block text-caption font-bold text-muted">Pueden recibirnos</span>
                                {resumenVentanas(p.ventanas).map(l => <span key={l} className="block text-label font-bold text-secondary">{l}</span>)}
                            </span>
                            {modo === 'empresa' && p.horarioEditable && p.ordenId && (
                                <button type="button" onClick={() => setCambiandoHorario(true)}
                                    className="shrink-0 h-8 px-3 rounded-lg bg-chip text-label font-black text-ink active:scale-95">Cambiar</button>
                            )}
                        </div>
                    )}
                    {p.detalle && <p className="px-1 text-body text-ink whitespace-pre-line">{p.detalle}</p>}
                    <VerFotos fotos={p.fotos} className="px-1" />
                </div>

                {noPuede && <ClienteNoPuedeSheet pedido={p} onCerrar={() => setNoPuede(false)}
                    onListo={(nuevo) => { setNoPuede(false); setP(nuevo); cargar(); onCambio && onCambio(); }} />}
                {hecho && <InformeTrabajo pedido={p} items={informe} />}
                {hecho && <TrabajoHecho p={p} modo={modo} funciones={funciones}
                    onListo={(nuevo) => { setP(nuevo); cargar(); onCambio && onCambio(); }} />}

                {/* Conversación */}
                <div className="space-y-2">
                    {/* Sin mensajes no se muestra nada: el cuadro de abajo ya invita a escribir */}
                    {comentarios.length > 0 && <p className="flex items-center gap-1.5 text-label font-black uppercase tracking-widest text-muted"><LuMessageCircle size={14} /> Conversación</p>}
                    {comentarios.map(c => {
                        const mio = modo === 'empresa' ? c.deEmpresa : !c.deEmpresa;
                        return (
                            <div key={c.id} className={`flex ${mio ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-[85%] px-3.5 py-2 rounded-2xl ${mio ? 'bg-[rgba(201,52,31,0.12)] rounded-br-md' : 'bg-chip rounded-bl-md'}`}>
                                    {!mio && <p className="text-[11px] font-black text-secondary">{c.autorNombre}</p>}
                                    <p className="text-body text-ink whitespace-pre-line break-words">{c.texto}</p>
                                    <p className="text-[10px] text-muted text-right">{haceCuanto(c.creadoEn)}</p>
                                </div>
                            </div>
                        );
                    })}
                    <div ref={finRef} />
                </div>
                {modo === 'admin' && p.estado !== 'HECHO' && (
                    <button type="button" onClick={() => setEliminando(true)}
                        className="w-full h-10 rounded-xl text-label font-black text-brand-red active:scale-95">Eliminar pedido (prueba o error)</button>
                )}
            </div>
            {eliminando && (
                <ConfirmDialog titulo="Eliminar pedido"
                    mensaje={`Se borra el pedido #${p.id} y su conversación${p.ordenId ? ', y la visita sale de la agenda del técnico' : ''}. A la empresa no le llega aviso. No se puede deshacer.`}
                    textoConfirmar="Sí, eliminar" onConfirmar={eliminar} onCancelar={() => setEliminando(false)} />
            )}
            {editando && (
                <NuevoPedidoSheet editar={p} onCerrar={() => setEditando(false)}
                    onCreado={(nuevo) => { setEditando(false); setP(nuevo); onCambio && onCambio(); }} />
            )}
            {/* Empresa: sin cobro en el momento, se factura en el cierre mensual */}
            {cerrando && (
                <CargaPorSerieSheet orden={cerrando} onClose={() => setCerrando(null)}
                    onGuardado={async () => {
                        try { await api.patch(`/ordenes/${cerrando.id}/estado`, { estado: 'COMPLETADA' }); }
                        catch { toast.error('El trabajo quedó cargado, pero no se pudo cerrar la visita'); }
                        cargar(); onCambio && onCambio();
                    }} />
            )}
            {eligiendoDia && (
                <ConfirmarHorarioSheet
                    servicio={{ id: eligiendoDia.presupuestoId, ventanasDisponibles: eligiendoDia.ventanasCliente, clienteNombre: eligiendoDia.clienteNombre }}
                    url={eligiendoDia.presupuestoId ? null : `/ordenes/${eligiendoDia.id}/confirmar-horario`}
                    onCerrar={() => setEligiendoDia(null)}
                    onConfirmado={async () => { setEligiendoDia(null); await cargar(); onCambio && onCambio(); }} />
            )}
            {cambiandoHorario && <CambiarHorarioSheet pedido={p} onCerrar={() => setCambiandoHorario(false)}
                onListo={(nuevo) => { setCambiandoHorario(false); setP(nuevo); onCambio && onCambio(); }} />}
        </ModalShell>
    );
}
